import React, { useState, useEffect, useCallback } from 'react';
import timeTrackingService from '../services/timeTrackingService';
import TimeTrackingNav from '../components/TimeTrackingNav';

const currentYear = new Date().getFullYear();

const METRIC_META = {
  units_produced:        { label: 'Units Produced',        unit: ' units',  defaultDir: 'above_is_good', badge: 'primary' },
  complaint_rate:        { label: 'Complaint Rate',         unit: '/10k',    defaultDir: 'below_is_good', badge: 'danger' },
  atp_pass_rate:         { label: 'ATP Pass Rate',          unit: '%',       defaultDir: 'above_is_good', badge: 'info' },
  ingredient_variance:   { label: 'Ingredient Variance',    unit: '%',       defaultDir: 'below_is_good', badge: 'warning' },
  labor_cost_per_unit:   { label: 'Labor Cost / Unit',      unit: '$/unit',  defaultDir: 'below_is_good', badge: 'secondary' },
  units_per_employee:    { label: 'Units / Employee',       unit: ' units',  defaultDir: 'above_is_good', badge: 'success' },
  labor_hrs_per_1k_units:{ label: 'Labor Hrs / 1k Units',   unit: ' hrs',    defaultDir: 'below_is_good', badge: 'dark' },
};

const EMPTY_RULE_FORM = { name: '', metricType: 'units_produced', targetValue: '', direction: 'above_is_good', tiers: [] };

const CATEGORY_LABELS = {
  sanitation: 'Sanitation', product_quality: 'Product Quality', waste: 'Waste',
  customer_complaint: 'Customer Complaint', compliance: 'Compliance', other: 'Other',
  performance_target: 'Performance Target',
};
const SEVERITY_COLORS = { minor: '#e6a817', major: '#fd7e14', critical: '#dc3545' };

// ── ConfirmModal ───────────────────────────────────────────────────────────────

function ConfirmModal({ show, title, message, confirmLabel = 'Delete', confirmVariant = 'danger', onConfirm, onCancel }) {
  if (!show) return null;
  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1060 }} onClick={onCancel}>
      <div className="modal-dialog modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header py-2">
            <h6 className="modal-title mb-0">{title}</h6>
            <button className="btn-close btn-close-sm" onClick={onCancel} />
          </div>
          {message && <div className="modal-body" style={{ fontSize: 13 }}>{message}</div>}
          <div className="modal-footer py-2">
            <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
            <button className={`btn btn-${confirmVariant} btn-sm`} onClick={onConfirm}>{confirmLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PolicyEditor ───────────────────────────────────────────────────────────────

function PolicyEditor({ onSaved }) {
  const [policy, setPolicy] = useState(null);
  const [form, setForm] = useState({ startingPoints: 100, tiers: [] });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    timeTrackingService.getPointPolicy()
      .then(d => { setPolicy(d.policy); setForm({ startingPoints: d.policy.startingPoints, tiers: d.policy.tiers || [] }); })
      .catch(() => {});
  }, []);

  const addTier = () => setForm(f => ({ ...f, tiers: [...f.tiers, { minPoints: '', bonusPct: '' }] }));
  const removeTier = (i) => setForm(f => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));
  const updateTier = (i, key, val) => setForm(f => ({
    ...f,
    tiers: f.tiers.map((t, idx) => idx === i ? { ...t, [key]: val } : t),
  }));

  const handleSave = async (e) => {
    e.preventDefault();
    setMsg(''); setErr(''); setSaving(true);
    try {
      const tiers = form.tiers.map(t => ({ minPoints: Number(t.minPoints), bonusPct: Number(t.bonusPct) }));
      await timeTrackingService.savePointPolicy({ startingPoints: Number(form.startingPoints), tiers });
      setMsg('Point policy saved.');
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.error || 'Save failed.');
    } finally { setSaving(false); }
  };

  if (!policy) return <div className="text-muted mb-3" style={{ fontSize: 13 }}>Loading policy…</div>;

  const tierSummary = [...(form.tiers)].sort((a, b) => Number(b.minPoints) - Number(a.minPoints))
    .map(t => `≥${t.minPoints} → ${t.bonusPct}%`).join(' · ');

  return (
    <div className="card mb-4">
      <div
        className="card-header d-flex align-items-center justify-content-between"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(v => !v)}
      >
        <span className="fw-semibold" style={{ fontSize: 14 }}>
          Point Policy
          {!open && tierSummary && (
            <span className="text-muted fw-normal ms-2" style={{ fontSize: 12 }}>{form.startingPoints} pts · {tierSummary}</span>
          )}
        </span>
        <span style={{ fontSize: 12, color: '#6c757d' }}>{open ? '▲ Collapse' : '▼ Edit'}</span>
      </div>
      {open && (
      <div className="card-body">
        {msg && <div className="alert alert-success py-2" style={{ fontSize: 13 }}>{msg}</div>}
        {err && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{err}</div>}
        <form onSubmit={handleSave}>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Starting Points</label>
              <input type="number" className="form-control form-control-sm" min="1" value={form.startingPoints} onChange={e => setForm(f => ({ ...f, startingPoints: e.target.value }))} />
            </div>
          </div>
          <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>Bonus Tiers</div>
          <div className="text-muted mb-2" style={{ fontSize: 12 }}>If employee has ≥ minPoints at year-end, they qualify for that tier's bonus %.</div>
          {form.tiers.length === 0 && <div className="text-muted" style={{ fontSize: 13 }}>No tiers yet. Add one below.</div>}
          {[...form.tiers].sort((a, b) => Number(b.minPoints) - Number(a.minPoints)).map((tier, i) => {
            const realIdx = form.tiers.indexOf(tier);
            return (
              <div key={realIdx} className="d-flex gap-2 align-items-center mb-2">
                <div style={{ width: 140 }}>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">≥</span>
                    <input type="number" className="form-control" placeholder="pts" value={tier.minPoints} onChange={e => updateTier(realIdx, 'minPoints', e.target.value)} />
                  </div>
                </div>
                <div style={{ width: 110 }}>
                  <div className="input-group input-group-sm">
                    <input type="number" className="form-control" step="0.1" placeholder="%" value={tier.bonusPct} onChange={e => updateTier(realIdx, 'bonusPct', e.target.value)} />
                    <span className="input-group-text">%</span>
                  </div>
                </div>
                <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => removeTier(realIdx)}>Remove</button>
              </div>
            );
          })}
          <div className="d-flex gap-2 mt-3">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addTier}>+ Add Tier</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
              Save Policy
            </button>
          </div>
        </form>
      </div>
      )}
    </div>
  );
}

// ── MetricRulesSection ─────────────────────────────────────────────────────────

function MetricRulesSection({ year, onApplied }) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedProgress, setExpandedProgress] = useState(new Set());
  const [granularity, setGranularity] = useState('monthly');
  const [progressCache, setProgressCache] = useState({});
  const [progressLoading, setProgressLoading] = useState(false);

  // edit modal
  const [editModal, setEditModal] = useState({ show: false, ruleId: null });
  const [editForm, setEditForm] = useState(EMPTY_RULE_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [editErr, setEditErr] = useState('');

  // delete confirm
  const [confirmDelete, setConfirmDelete] = useState({ show: false, ruleId: null, name: '' });

  // apply modal
  const [applyModal, setApplyModal] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [applying, setApplying] = useState(false);
  const [applyErr, setApplyErr] = useState('');
  const [applyMsg, setApplyMsg] = useState('');

  const loadRules = useCallback(() => {
    setLoading(true);
    timeTrackingService.getMetricRules(year)
      .then(d => setRules(d.rules || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => { loadRules(); }, [loadRules]);

  const loadProgress = useCallback((yr, gran) => {
    const key = `${yr}-${gran}`;
    if (progressCache[key]) return;
    setProgressLoading(true);
    timeTrackingService.getMetricProgress(yr, gran)
      .then(d => setProgressCache(c => ({ ...c, [key]: d.rules || [] })))
      .catch(() => {})
      .finally(() => setProgressLoading(false));
  }, [progressCache]);

  const toggleProgress = (ruleId) => {
    const next = new Set(expandedProgress);
    if (next.has(ruleId)) {
      next.delete(ruleId);
    } else {
      next.add(ruleId);
      loadProgress(year, granularity);
    }
    setExpandedProgress(next);
  };

  const changeGranularity = (gran) => {
    setGranularity(gran);
    if (expandedProgress.size > 0) loadProgress(year, gran);
  };

  const openNew = (e) => {
    e.stopPropagation();
    setEditForm({ ...EMPTY_RULE_FORM });
    setEditErr('');
    setEditModal({ show: true, ruleId: null });
  };

  const openEdit = (rule) => {
    setEditForm({
      name: rule.name,
      metricType: rule.metricType,
      targetValue: rule.targetValue != null ? String(rule.targetValue) : '',
      direction: rule.direction,
      tiers: rule.tiers.map(t => ({ threshold: String(t.threshold), pointDeduction: String(t.pointDeduction) })),
    });
    setEditErr('');
    setEditModal({ show: true, ruleId: rule.id });
  };

  const handleEditSave = async () => {
    setEditErr('');
    if (!editForm.name.trim() || !editForm.targetValue) {
      setEditErr('Name and target value are required.');
      return;
    }
    const tiers = editForm.tiers.map(t => ({ threshold: Number(t.threshold), pointDeduction: Number(t.pointDeduction) }));
    const payload = {
      name: editForm.name.trim(),
      metricType: editForm.metricType,
      targetValue: editForm.targetValue !== '' ? Number(editForm.targetValue) : null,
      direction: editForm.direction,
      tiers,
    };
    setEditSaving(true);
    try {
      if (editModal.ruleId) {
        await timeTrackingService.updateMetricRule(editModal.ruleId, payload);
      } else {
        await timeTrackingService.createMetricRule(payload);
      }
      setEditModal({ show: false, ruleId: null });
      setProgressCache({});
      loadRules();
    } catch (e) {
      setEditErr(e?.response?.data?.error || 'Save failed.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await timeTrackingService.deleteMetricRule(confirmDelete.ruleId);
      setConfirmDelete({ show: false, ruleId: null, name: '' });
      loadRules();
    } catch (e) {}
  };

  const openApply = (e) => {
    e.stopPropagation();
    const initOverrides = {};
    rules.forEach(r => { initOverrides[r.id] = { bypassed: false, bypassReason: '' }; });
    setOverrides(initOverrides);
    setApplyErr('');
    setApplyMsg('');
    setApplyModal(true);
  };

  const handleApply = async () => {
    setApplyErr('');
    setApplying(true);
    try {
      const result = await timeTrackingService.applyMetricDeductions(year, overrides);
      const applied = (result.results || []).filter(r => !r.skipped).length;
      const skipped = (result.results || []).filter(r => r.skipped).length;
      setApplyMsg(`Done. ${applied} rule(s) applied${skipped > 0 ? `, ${skipped} already applied` : ''}.`);
      loadRules();
      if (onApplied) onApplied();
    } catch (e) {
      setApplyErr(e?.response?.data?.error || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  const achColor = (pct) => {
    if (pct == null) return '#6c757d';
    return pct >= 90 ? '#198754' : pct >= 70 ? '#fd7e14' : '#dc3545';
  };

  const fmtActual = (metricType, val, unit) => {
    if (val == null) return '—';
    if (metricType === 'units_produced') return `${Number(val).toLocaleString()}${unit}`;
    return `${Number(val).toFixed(2)}${unit}`;
  };

  const progressForRule = (ruleId) => {
    const key = `${year}-${granularity}`;
    return (progressCache[key] || []).find(r => r.id === ruleId);
  };

  const addEditTier = () => setEditForm(f => ({ ...f, tiers: [...f.tiers, { threshold: '', pointDeduction: '' }] }));
  const removeEditTier = (i) => setEditForm(f => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));
  const updateEditTier = (i, key, val) => setEditForm(f => ({
    ...f, tiers: f.tiers.map((t, idx) => idx === i ? { ...t, [key]: val } : t),
  }));

  const appliedCount = rules.filter(r => r.application?.appliedAt).length;

  return (
    <>
      <div className="card mb-4">
        <div
          className="card-header d-flex align-items-center justify-content-between"
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setOpen(v => !v)}
        >
          <span className="fw-semibold" style={{ fontSize: 14 }}>
            Performance Metric Rules
            {!open && rules.length > 0 && (
              <span className="text-muted fw-normal ms-2" style={{ fontSize: 12 }}>
                {rules.length} rule{rules.length !== 1 ? 's' : ''}
                {appliedCount > 0 ? ` · ${appliedCount} applied` : ''}
              </span>
            )}
          </span>
          <span style={{ fontSize: 12, color: '#6c757d' }}>{open ? '▲ Collapse' : '▼ Expand'}</span>
        </div>

        {open && (
          <div className="card-body">
            {loading ? (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Auto-pull analytics metrics and deduct points company-wide based on year-end achievement.
                  </div>
                  <div className="d-flex gap-2">
                    {rules.length > 0 && (
                      <button className="btn btn-outline-success btn-sm" onClick={openApply}>
                        Review & Apply {year}
                      </button>
                    )}
                    <button className="btn btn-outline-primary btn-sm" onClick={openNew}>+ Add Rule</button>
                  </div>
                </div>

                {rules.length === 0 ? (
                  <div className="text-muted text-center py-3" style={{ fontSize: 13 }}>
                    No metric rules yet. Add one to track performance targets.
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span className="text-muted" style={{ fontSize: 12 }}>Progress view:</span>
                      {['monthly', 'quarterly'].map(g => (
                        <button
                          key={g}
                          className={`btn btn-sm ${granularity === g ? 'btn-secondary' : 'btn-outline-secondary'}`}
                          style={{ fontSize: 11, padding: '2px 10px' }}
                          onClick={() => changeGranularity(g)}
                        >
                          {g.charAt(0).toUpperCase() + g.slice(1)}
                        </button>
                      ))}
                    </div>

                    {rules.map(rule => {
                      const meta = METRIC_META[rule.metricType] || { label: rule.metricType, unit: '', badge: 'secondary' };
                      const showProgress = expandedProgress.has(rule.id);
                      const progData = progressForRule(rule.id);
                      const isApplied = !!rule.application?.appliedAt;

                      return (
                        <div key={rule.id} className="border rounded p-3 mb-2">
                          <div className="d-flex align-items-start gap-3">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                <span className={`badge text-bg-${meta.badge}`} style={{ fontSize: 10 }}>{meta.label}</span>
                                <span className="fw-semibold" style={{ fontSize: 13 }}>{rule.name}</span>
                                {isApplied && <span className="badge text-bg-success" style={{ fontSize: 10 }}>Applied</span>}
                                {isApplied && rule.application.bypassed && (
                                  <span className="badge text-bg-secondary" style={{ fontSize: 10 }}>Bypassed</span>
                                )}
                              </div>
                              <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                                Target: {Number(rule.targetValue).toLocaleString()}{meta.unit}
                                {' · '}
                                {rule.direction === 'above_is_good' ? 'Higher is better' : 'Lower is better'}
                                {rule.tiers.length > 0 && (
                                  <span className="ms-2">
                                    · Tiers: {[...rule.tiers].sort((a, b) => Number(b.threshold) - Number(a.threshold))
                                      .map(t => `${rule.direction === 'above_is_good' ? '<' : '>'}${Number(t.threshold).toLocaleString()}${meta.unit} → -${t.pointDeduction}pts`)
                                      .join(', ')}
                                  </span>
                                )}
                              </div>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <div style={{ width: 200 }}>
                                  <div style={{ height: 8, background: '#e9ecef', borderRadius: 4, overflow: 'hidden' }}>
                                    <div style={{
                                      height: '100%',
                                      width: `${Math.min(100, rule.achievementPct || 0)}%`,
                                      background: achColor(rule.achievementPct),
                                      borderRadius: 4,
                                      transition: 'width 0.3s',
                                    }} />
                                  </div>
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 700, color: achColor(rule.achievementPct) }}>
                                  {rule.achievementPct != null ? `${Number(rule.achievementPct).toFixed(1)}%` : '—'}
                                </span>
                                <span className="text-muted" style={{ fontSize: 11 }}>
                                  YTD: {fmtActual(rule.metricType, rule.actual, meta.unit)}
                                </span>
                                {rule.projectedDeduction > 0 && !isApplied && (
                                  <span className="badge text-bg-warning text-dark" style={{ fontSize: 10 }}>
                                    -{rule.projectedDeduction} pts/employee projected
                                  </span>
                                )}
                                {isApplied && rule.application.pointsDeducted > 0 && (
                                  <span className="badge text-bg-danger" style={{ fontSize: 10 }}>
                                    -{rule.application.pointsDeducted} pts deducted
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="d-flex gap-1 flex-shrink-0">
                              <button
                                className={`btn btn-sm ${showProgress ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                style={{ fontSize: 11 }}
                                onClick={() => toggleProgress(rule.id)}
                              >
                                {showProgress ? 'Hide' : 'Progress'}
                              </button>
                              <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => openEdit(rule)}>Edit</button>
                              <button className="btn btn-outline-danger btn-sm" style={{ fontSize: 11 }} onClick={() => setConfirmDelete({ show: true, ruleId: rule.id, name: rule.name })}>✕</button>
                            </div>
                          </div>

                          {showProgress && (
                            <div className="mt-3 pt-2 border-top">
                              {progressLoading && !progData ? (
                                <div className="text-center py-2"><div className="spinner-border spinner-border-sm" /></div>
                              ) : progData ? (
                                <div className="table-responsive">
                                  <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                                    <thead className="table-light">
                                      <tr>
                                        <th>Period</th>
                                        <th>Actual</th>
                                        <th>Target</th>
                                        <th>Achievement</th>
                                        <th>Points</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {progData.periods.map(p => (
                                        <tr key={p.label} className={p.isFuture ? 'text-muted' : ''}>
                                          <td>{p.label}</td>
                                          <td>{p.isFuture ? '—' : fmtActual(rule.metricType, p.actual, meta.unit)}</td>
                                          <td>{Number(rule.targetValue).toLocaleString()}{meta.unit}</td>
                                          <td>
                                            {!p.isFuture && p.achievementPct != null ? (
                                              <span style={{ fontWeight: 600, color: achColor(p.achievementPct) }}>
                                                {Number(p.achievementPct).toFixed(1)}%
                                              </span>
                                            ) : '—'}
                                          </td>
                                          <td>
                                            {!p.isFuture && p.deduction != null ? (
                                              p.deduction > 0
                                                ? <span className="text-danger fw-semibold">-{p.deduction}</span>
                                                : <span className="text-success">0</span>
                                            ) : '—'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-muted text-center" style={{ fontSize: 12 }}>No progress data available.</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        show={confirmDelete.show}
        title="Archive Rule"
        message={<>Archive <strong>{confirmDelete.name}</strong>? It will no longer be active or tracked.</>}
        confirmLabel="Archive"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete({ show: false, ruleId: null, name: '' })}
      />

      {/* Edit / New Rule Modal */}
      {editModal.show && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1055 }} onClick={() => setEditModal({ show: false, ruleId: null })}>
          <div className="modal-dialog" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title mb-0">{editModal.ruleId ? 'Edit Rule' : 'New Metric Rule'}</h6>
                <button className="btn-close" onClick={() => setEditModal({ show: false, ruleId: null })} />
              </div>
              <div className="modal-body" style={{ fontSize: 13 }}>
                {editErr && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{editErr}</div>}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Rule Name</label>
                  <input
                    className="form-control form-control-sm"
                    value={editForm.name}
                    onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Annual Units Target"
                  />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-7">
                    <label className="form-label fw-semibold">Metric</label>
                    <select
                      className="form-select form-select-sm"
                      value={editForm.metricType}
                      onChange={e => {
                        const mt = e.target.value;
                        setEditForm(f => ({ ...f, metricType: mt, direction: METRIC_META[mt]?.defaultDir || 'above_is_good' }));
                      }}
                    >
                      {Object.entries(METRIC_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                    </select>
                  </div>
                  <div className="col-5">
                    <label className="form-label fw-semibold">
                      Goal ({(METRIC_META[editForm.metricType]?.unit || '').trim() || 'value'}) <span className="text-muted fw-normal" style={{ fontSize: 11 }}>optional</span>
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editForm.targetValue}
                      onChange={e => setEditForm(f => ({ ...f, targetValue: e.target.value }))}
                      placeholder="e.g. 5"
                      step="any"
                      min="0"
                    />
                  </div>
                </div>
                <div className="alert alert-light py-2 mb-3" style={{ fontSize: 12 }}>
                  {editForm.direction === 'above_is_good'
                    ? <>Higher is better. Deduct points when actual falls <strong>below</strong> a threshold.</>
                    : <>Lower is better. Deduct points when actual goes <strong>above</strong> a threshold.</>}
                  {editForm.targetValue && <span className="ms-2 text-muted">Goal of {editForm.targetValue}{(METRIC_META[editForm.metricType]?.unit||'').trim()} is shown on the progress bar only.</span>}
                </div>

                <div className="fw-semibold mb-1">Deduction Tiers</div>
                <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                  Set absolute thresholds in the same unit as the metric.
                  The worst applicable tier wins when multiple thresholds are breached.
                </div>
                {editForm.tiers.length === 0 && (
                  <div className="text-muted mb-2" style={{ fontSize: 12 }}>No tiers — rule tracks progress only, no deductions applied.</div>
                )}
                {editForm.tiers.map((t, i) => (
                  <div key={i} className="d-flex gap-2 align-items-center mb-2">
                    <div className="input-group input-group-sm" style={{ width: 175 }}>
                      <span className="input-group-text" style={{ fontSize: 11 }}>
                        {editForm.direction === 'above_is_good' ? 'below' : 'above'}
                      </span>
                      <input
                        type="number"
                        className="form-control"
                        placeholder={(METRIC_META[editForm.metricType]?.unit || '').trim() || 'value'}
                        value={t.threshold}
                        onChange={e => updateEditTier(i, 'threshold', e.target.value)}
                        step="any"
                        min="0"
                      />
                      <span className="input-group-text" style={{ fontSize: 11 }}>
                        {(METRIC_META[editForm.metricType]?.unit || '').trim() || ''}
                      </span>
                    </div>
                    <div className="input-group input-group-sm" style={{ width: 120 }}>
                      <span className="input-group-text" style={{ fontSize: 11 }}>−</span>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="pts"
                        value={t.pointDeduction}
                        onChange={e => updateEditTier(i, 'pointDeduction', e.target.value)}
                        min="0"
                      />
                      <span className="input-group-text" style={{ fontSize: 11 }}>pts</span>
                    </div>
                    <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => removeEditTier(i)}>✕</button>
                  </div>
                ))}
                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                  {editForm.direction === 'above_is_good'
                    ? 'Example: below 90,000 units → -2 pts (moderate miss), below 70,000 units → -4 pts (severe miss).'
                    : 'Example: above 7% → -2 pts (moderate), above 10% → -4 pts (severe).'}
                </div>
                <button type="button" className="btn btn-outline-secondary btn-sm mt-2" onClick={addEditTier}>+ Add Tier</button>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setEditModal({ show: false, ruleId: null })}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleEditSave} disabled={editSaving}>
                  {editSaving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                  {editModal.ruleId ? 'Save Changes' : 'Create Rule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review & Apply Modal */}
      {applyModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1055 }} onClick={() => { if (!applying) setApplyModal(false); }}>
          <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title mb-0">Review & Apply Year-End Deductions — {year}</h6>
                <button className="btn-close" onClick={() => setApplyModal(false)} disabled={applying} />
              </div>
              <div className="modal-body" style={{ fontSize: 13 }}>
                {applyErr && <div className="alert alert-danger py-2">{applyErr}</div>}
                {applyMsg && <div className="alert alert-success py-2">{applyMsg}</div>}
                {!applyMsg && (
                  <p className="text-muted" style={{ fontSize: 12 }}>
                    Review each rule's YTD performance. Toggle <strong>Bypass</strong> to skip a rule.
                    Applying will create a "Performance Target" derogatory mark for all active employees on rules that missed their target.
                    Already-applied rules are skipped automatically.
                  </p>
                )}
                {rules.map(rule => {
                  const meta = METRIC_META[rule.metricType] || { label: rule.metricType, unit: '' };
                  const ov = overrides[rule.id] || {};
                  const isApplied = !!rule.application?.appliedAt;
                  const effectiveDeduction = ov.bypassed ? 0 : rule.projectedDeduction;

                  return (
                    <div key={rule.id} className={`border rounded p-3 mb-2${ov.bypassed ? ' opacity-50' : ''}`}>
                      <div className="d-flex align-items-start justify-content-between gap-3">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                            <span className="fw-semibold">{rule.name}</span>
                            <span className={`badge text-bg-${meta.badge}`} style={{ fontSize: 10 }}>{meta.label}</span>
                            {isApplied && <span className="badge text-bg-secondary" style={{ fontSize: 10 }}>Will re-apply</span>}
                          </div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            Target: {Number(rule.targetValue).toLocaleString()}{meta.unit}
                            {' · '}
                            YTD: {fmtActual(rule.metricType, rule.actual, meta.unit)}
                            {' · '}
                            Achievement: <span style={{ fontWeight: 700, color: achColor(rule.achievementPct) }}>
                              {rule.achievementPct != null ? `${Number(rule.achievementPct).toFixed(1)}%` : '—'}
                            </span>
                          </div>
                          <div className="mt-1" style={{ fontSize: 12 }}>
                            {effectiveDeduction > 0
                              ? <span className="text-danger fw-semibold">Will deduct {effectiveDeduction} pts per employee</span>
                              : <span className="text-success fw-semibold">On track — no deduction</span>}
                          </div>
                        </div>
                        <div className="form-check form-switch flex-shrink-0 mt-1">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`bypass-${rule.id}`}
                            checked={!!ov.bypassed}
                            onChange={e => setOverrides(o => ({ ...o, [rule.id]: { ...o[rule.id], bypassed: e.target.checked } }))}
                          />
                          <label className="form-check-label" htmlFor={`bypass-${rule.id}`} style={{ fontSize: 12 }}>Bypass</label>
                        </div>
                      </div>
                      {ov.bypassed && (
                        <div className="mt-2">
                          <input
                            className="form-control form-control-sm"
                            placeholder="Bypass reason (optional)"
                            value={ov.bypassReason || ''}
                            onChange={e => setOverrides(o => ({ ...o, [rule.id]: { ...o[rule.id], bypassReason: e.target.value } }))}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setApplyModal(false)} disabled={applying}>
                  {applyMsg ? 'Close' : 'Cancel'}
                </button>
                {!applyMsg && (
                  <button className="btn btn-danger btn-sm" onClick={handleApply} disabled={applying}>
                    {applying
                      ? <><span className="spinner-border spinner-border-sm me-1" />Applying…</>
                      : `Confirm & Apply ${year}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── YearEndBonusPage ───────────────────────────────────────────────────────────

function fmtHrsMin(minutes) {
  if (!minutes) return '0h 0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

export default function YearEndBonusPage() {
  const [year, setYear] = useState(currentYear);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [policyKey, setPolicyKey] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [eventsByEmp, setEventsByEmp] = useState({}); // empId → [] | 'loading'

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setEventsByEmp(prev => {
      if (prev[id] !== undefined) return prev;
      const next = { ...prev, [id]: 'loading' };
      timeTrackingService.getEmployeeEvents(id)
        .then(evs => setEventsByEmp(m => ({ ...m, [id]: evs || [] })))
        .catch(() => setEventsByEmp(m => ({ ...m, [id]: [] })));
      return next;
    });
  };

  const loadReport = useCallback(() => {
    setLoading(true);
    timeTrackingService.getYearEndReport(year)
      .then(setReport).catch(() => setReport(null)).finally(() => setLoading(false));
  }, [year]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const pointColor = (p, max) => {
    const pct = max > 0 ? p / max : 0;
    return pct >= 0.9 ? '#198754' : pct >= 0.75 ? '#e6a817' : '#dc3545';
  };

  return (
    <div className="container mt-4">
      <TimeTrackingNav />
      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <h5 className="mb-0">Year-End Bonus Report</h5>
        <select className="form-select form-select-sm" style={{ width: 100 }} value={year} onChange={e => setYear(Number(e.target.value))}>
          {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <PolicyEditor onSaved={() => { setPolicyKey(k => k + 1); loadReport(); }} key={policyKey} />

      <MetricRulesSection year={year} onApplied={loadReport} />

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" /></div>
      ) : !report ? (
        <div className="alert alert-warning">Could not load report.</div>
      ) : (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <div className="card text-center py-3">
                <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Employees</div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>{report.results?.length || 0}</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center py-3">
                <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Est. Bonus</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>
                  ${(report.results || []).reduce((s, r) => s + (r.bonusPayout || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center py-3">
                <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Avg Points</div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>
                  {report.results?.length ? Math.round(report.results.reduce((s, r) => s + r.points, 0) / report.results.length) : 0}
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center py-3">
                <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Marks</div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>
                  {(report.results || []).reduce((s, r) => s + r.marksCount, 0)}
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive mb-5">
            <table className="table table-hover align-middle" style={{ fontSize: 13 }}>
              <thead className="table-light">
                <tr>
                  <th style={{ width: 28 }} />
                  <th>Employee</th>
                  <th>Points</th>
                  <th>Marks</th>
                  <th>Tier</th>
                  <th className="text-end">Est. Bonus</th>
                </tr>
              </thead>
              <tbody>
                {(report.results || []).map(r => {
                  const isExpanded = expandedRows.has(r.employee.id);
                  const empEvents = eventsByEmp[r.employee.id];
                  const yearEvents = Array.isArray(empEvents)
                    ? empEvents.filter(ev => {
                        const d = ev.event.startDate || '';
                        return d.startsWith(String(year));
                      })
                    : [];
                  return (
                    <React.Fragment key={r.employee.id}>
                      <tr
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleRow(r.employee.id)}
                      >
                        <td style={{ color: '#6c757d', fontSize: 11 }}>
                          {isExpanded ? '▼' : '▶'}
                        </td>
                        <td className="fw-semibold">{r.employee.firstName} {r.employee.lastName}</td>
                        <td>
                          <span style={{ fontWeight: 700, color: pointColor(r.points, report.startingPoints) }}>
                            {r.points}
                          </span>
                          <span className="text-muted"> / {report.startingPoints}</span>
                        </td>
                        <td>
                          {r.marksCount > 0 ? (
                            <span className="text-danger">{r.marksCount} mark{r.marksCount !== 1 ? 's' : ''}</span>
                          ) : (
                            <span className="text-muted">None</span>
                          )}
                        </td>
                        <td>
                          {r.bonusPct > 0 ? (
                            <span className="badge text-bg-success">{r.bonusPct}%</span>
                          ) : (
                            <span className="text-muted">No tier</span>
                          )}
                        </td>
                        <td className="text-end fw-semibold">
                          {r.bonusPayout != null
                            ? `$${Number(r.bonusPayout).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : <span className="text-muted">—</span>}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td />
                          <td colSpan={5} style={{ background: '#f8f9fa', padding: '8px 12px 16px' }}>

                            {/* ── Event participation ── */}
                            <div style={{ fontSize: 12, color: '#495057', marginBottom: 6, fontWeight: 600 }}>
                              Event Participation — {year}
                            </div>
                            {empEvents === 'loading' ? (
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                <span className="spinner-border spinner-border-sm me-1" />Loading events…
                              </div>
                            ) : yearEvents.length === 0 ? (
                              <div className="text-muted" style={{ fontSize: 12 }}>No events in {year}.</div>
                            ) : (
                              <table className="table table-sm mb-3" style={{ fontSize: 12, background: 'transparent' }}>
                                <thead>
                                  <tr className="text-muted" style={{ fontSize: 11 }}>
                                    <th style={{ fontWeight: 500 }}>Event</th>
                                    <th style={{ fontWeight: 500 }}>Dates</th>
                                    <th style={{ fontWeight: 500 }}>Role</th>
                                    <th style={{ fontWeight: 500 }}>Tags</th>
                                    <th style={{ fontWeight: 500 }} className="text-end">Days</th>
                                    <th style={{ fontWeight: 500 }} className="text-end">Sched. Hours</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {yearEvents.map(ev => (
                                    <tr key={ev.event.id}>
                                      <td className="fw-semibold">{ev.event.name}</td>
                                      <td className="text-muted">
                                        {ev.event.startDate?.slice(0,10)} – {ev.event.endDate?.slice(0,10)}
                                      </td>
                                      <td>
                                        {ev.isInventoryManager
                                          ? <span className="badge bg-info text-dark" style={{ fontSize: 10 }}>Inv. Mgr</span>
                                          : <span className="text-muted">Staff</span>}
                                      </td>
                                      <td>
                                        {ev.tags
                                          ? ev.tags.split(',').map(t => (
                                              <span key={t} className="badge bg-light text-dark border me-1" style={{ fontSize: 9 }}>{t.trim()}</span>
                                            ))
                                          : <span className="text-muted">—</span>}
                                      </td>
                                      <td className="text-end">{ev.totalDays}</td>
                                      <td className="text-end fw-semibold">{fmtHrsMin(ev.totalMinutes)}</td>
                                    </tr>
                                  ))}
                                  {yearEvents.length > 1 && (
                                    <tr className="table-light fw-semibold">
                                      <td colSpan={4} className="text-end text-muted" style={{ fontSize: 11 }}>Total</td>
                                      <td className="text-end">{yearEvents.reduce((s, e) => s + e.totalDays, 0)}</td>
                                      <td className="text-end">{fmtHrsMin(yearEvents.reduce((s, e) => s + e.totalMinutes, 0))}</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            )}

                            {/* ── Deductions breakdown ── */}
                            {r.marksCount > 0 && (
                              <>
                                <div style={{ fontSize: 12, color: '#495057', marginBottom: 6, fontWeight: 600 }}>
                                  Deductions breakdown
                                </div>
                                <table className="table table-sm mb-0" style={{ fontSize: 12, background: 'transparent' }}>
                                  <thead>
                                    <tr className="text-muted" style={{ fontSize: 11 }}>
                                      <th style={{ fontWeight: 500 }}>Category</th>
                                      <th style={{ fontWeight: 500 }}>Severity</th>
                                      <th style={{ fontWeight: 500 }}>Points</th>
                                      <th style={{ fontWeight: 500 }}>Date</th>
                                      <th style={{ fontWeight: 500 }}>Notes</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[...(r.marks || [])].sort((a, b) => new Date(b.date) - new Date(a.date)).map(m => (
                                      <tr key={m.id}>
                                        <td>{CATEGORY_LABELS[m.category] || m.category}</td>
                                        <td>
                                          <span style={{ color: SEVERITY_COLORS[m.severity], fontWeight: 600 }}>
                                            {m.severity}
                                          </span>
                                        </td>
                                        <td className="text-danger fw-semibold">−{m.pointsDeducted}</td>
                                        <td className="text-muted">{m.date}</td>
                                        <td className="text-muted" style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {m.notes || '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
